import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AwardToastContext = createContext(null);

export const useAwardToast = () => {
  const ctx = useContext(AwardToastContext);
  if (!ctx) throw new Error('useAwardToast must be used within AwardToastProvider');
  return ctx;
};

// ─── Send award email via Supabase Edge Function ──────────────────────────────
async function sendAwardEmail(award) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user?.email) return;

    const { data: profile } = await supabase?.from('user_profiles')?.select('first_name, username')?.eq('id', user?.id)?.maybeSingle();

    const displayName = profile?.first_name || profile?.username || 'Faithful Student';

    await supabase?.functions?.invoke('send-award-email', {
      body: {
        to: user?.email,
        displayName,
        awardName: award?.name,
        awardDescription: award?.description,
        awardIcon: award?.icon,
        goldReward: award?.goldReward || 0,
        silverReward: award?.silverReward || 0,
      }
    });
  } catch (err) {
    // Non-critical — don't block UI
    console.warn('Award email send failed:', err?.message);
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export const AwardToastProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const isShowingRef = useRef(false);

  // Advance to next item in queue
  const advance = useCallback((remainingQueue) => {
    if (remainingQueue?.length === 0) {
      setCurrent(null);
      isShowingRef.current = false;
      return;
    }
    const [next] = remainingQueue;
    const restQueue = remainingQueue?.slice(1);
    setCurrent(next);
    setQueue(restQueue);
    // Fire email for each award shown
    sendAwardEmail(next);
  }, []);

  const showAwardToast = useCallback((award) => {
    if (!isShowingRef?.current) {
      isShowingRef.current = true;
      setCurrent(award);
      setQueue([]);
      sendAwardEmail(award);
    } else {
      setQueue(prev => [...prev, award]);
    }
  }, []);

  // Show multiple awards sequentially
  const showAwardToasts = useCallback((awards) => {
    if (!awards?.length) return;
    const [first] = awards;
    const restAwards = awards?.slice(1);
    if (!isShowingRef?.current) {
      isShowingRef.current = true;
      setCurrent(first);
      setQueue(restAwards);
      sendAwardEmail(first);
    } else {
      setQueue(prev => [...prev, ...awards]);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setQueue(prev => {
      const next = prev[0];
      const rest = prev.slice(1);
      if (next) {
        setCurrent(next);
        sendAwardEmail(next);
      } else {
        setCurrent(null);
        isShowingRef.current = false;
      }
      return rest;
    });
  }, []);

  return (
    <AwardToastContext.Provider value={{ showAwardToast, showAwardToasts }}>
      {children}
      {current && (
        <AwardToastDisplay
          award={current}
          queueLength={queue?.length}
          onDismiss={handleDismiss}
        />
      )}
    </AwardToastContext.Provider>
  );
};

// ─── Coin Animation Component ─────────────────────────────────────────────────
const CoinBurst = ({ type, amount }) => {
  const isGold = type === 'gold';
  return (
    <div className="relative inline-flex items-center gap-1.5">
      <div className={`relative flex items-center justify-center w-8 h-8 rounded-full shadow-lg ${
        isGold
          ? 'bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-600 border-2 border-yellow-500' :'bg-gradient-to-br from-gray-200 via-slate-300 to-gray-500 border-2 border-gray-400'
      } coin-bounce`}>
        <span className="text-sm font-black" style={{ color: isGold ? '#7c4a00' : '#374151' }}>
          {isGold ? '✦' : '✧'}
        </span>
      </div>
      <span className={`text-sm font-bold ${isGold ? 'text-yellow-300' : 'text-gray-300'}`}>
        +{amount} {isGold ? 'Gold' : 'Silver'}
      </span>
    </div>
  );
};

// ─── Toast Display Component ──────────────────────────────────────────────────
const DURATION = 4000;

const AwardToastDisplay = ({ award, queueLength, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  const remainingRef = useRef(DURATION);
  const startTimeRef = useRef(null);

  const clearTimers = () => {
    if (timerRef?.current) clearTimeout(timerRef?.current);
    if (intervalRef?.current) clearInterval(intervalRef?.current);
  };

  const startProgressInterval = (duration) => {
    const startPct = (remainingRef?.current / DURATION) * 100;
    const tickMs = 50;
    const decrement = (tickMs / duration) * startPct;
    intervalRef.current = setInterval(() => {
      setProgress(prev => Math.max(0, prev - decrement));
    }, tickMs);
  };

  const startAutoTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    startProgressInterval(remainingRef?.current);
    timerRef.current = setTimeout(() => {
      handleClose();
    }, remainingRef?.current);
  }, []);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 50);
    startAutoTimer();
    return () => {
      clearTimeout(showTimer);
      clearTimers();
    };
  }, [award]);

  const handlePause = () => {
    if (paused) return;
    setPaused(true);
    clearTimers();
    const elapsed = Date.now() - (startTimeRef?.current || Date.now());
    remainingRef.current = Math.max(0, remainingRef?.current - elapsed);
  };

  const handleResume = () => {
    if (!paused) return;
    setPaused(false);
    startAutoTimer();
  };

  const handleClose = () => {
    clearTimers();
    setVisible(false);
    setTimeout(() => onDismiss(), 300);
  };

  const hasGold = (award?.goldReward || 0) > 0;
  const hasSilver = (award?.silverReward || 0) > 0;
  const hasReward = hasGold || hasSilver;

  return (
    <>
      <style>{`
        @keyframes coinBounce {
          0%   { transform: scale(0) rotate(-15deg); opacity: 0; }
          50%  { transform: scale(1.3) rotate(8deg); opacity: 1; }
          70%  { transform: scale(0.9) rotate(-4deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes toastSlideUp {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes toastSlideDown {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(24px); opacity: 0; }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .coin-bounce {
          animation: coinBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
        .toast-enter { animation: toastSlideUp 0.3s ease-out forwards; }
        .toast-exit  { animation: toastSlideDown 0.3s ease-in forwards; }
        .award-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.15) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: shimmer 2s linear infinite;
        }
      `}</style>
      <div
        className={`fixed bottom-6 right-6 z-[9999] ${visible ? 'toast-enter' : 'toast-exit'}`}
        style={{ maxWidth: 'calc(100vw - 3rem)' }}
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
        onTouchStart={handlePause}
        onTouchEnd={handleResume}
      >
        <div className="relative w-80 rounded-2xl overflow-hidden shadow-2xl border border-amber-500/60"
          style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' }}>

          {/* Shimmer overlay */}
          <div className="absolute inset-0 award-shimmer pointer-events-none" />

          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600" />

          <div className="relative p-5">
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">
                  🏆 Award Earned!
                </span>
                {queueLength > 0 && (
                  <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">
                    +{queueLength} more
                  </span>
                )}
              </div>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-300 transition-colors p-0.5"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Award body */}
            <div className="flex items-start gap-4">
              <div className="text-5xl flex-shrink-0 leading-none drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.5))' }}>
                {award?.icon || '🏆'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-white leading-tight">{award?.name}</h3>
                <p className="text-xs text-gray-300 mt-1 leading-snug">{award?.description}</p>

                {/* Coin rewards */}
                {hasReward && (
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    {hasGold && (
                      <div className="bg-yellow-900/30 border border-yellow-600/40 rounded-xl px-3 py-1.5">
                        <CoinBurst type="gold" amount={award?.goldReward} />
                      </div>
                    )}
                    {hasSilver && (
                      <div className="bg-slate-700/40 border border-slate-500/40 rounded-xl px-3 py-1.5">
                        <CoinBurst type="silver" amount={award?.silverReward} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-1 bg-gray-700/60 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-none"
                style={{
                  width: `${progress}%`,
                  background: paused
                    ? 'rgba(251,191,36,0.4)'
                    : 'linear-gradient(90deg, #d97706, #fbbf24)',
                  transition: paused ? 'none' : undefined
                }}
              />
            </div>
            {paused && (
              <p className="text-center text-xs text-gray-500 mt-1">Paused — move away to resume</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AwardToastProvider;
